import { ArrowRight, CheckCircle, TrendingUp, Zap, Target, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useNavigate, Link } from "react-router-dom";
import { PageHead } from "@/components/seo/PageHead";

const MarquesDnvb = () => {
  const navigate = useNavigate();

  return (
    <>
      <PageHead
        title="Amazon pour DNVB & Marques digitales — Ads, A+, UE/US | Cosmos Wave"
        description="Accélérer et structurer votre croissance Amazon. Contenu clair, Ads pilotées au profit, expansion UE/US sans friction."
        canonical="https://www.cosmos-wave.com/marques-dnvb"
        pageType="service"
      />
      <main className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-12 lg:py-16 xl:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-background" />
        <div className="container mx-auto px-6 xl:px-8 2xl:px-12 relative">
          <div className="max-w-4xl xl:max-w-5xl mx-auto text-center">
            <Badge variant="outline" className="text-accent border-accent/30 bg-accent/10 mb-6">
              Profil · DNVB & Marques digitales
            </Badge>
            <h1 className="text-4xl md:text-5xl xl:text-6xl 2xl:text-7xl font-bold tracking-tight mb-6 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              Accélérer et structurer votre croissance Amazon
            </h1>
            <p className="text-xl xl:text-2xl text-muted-foreground mb-8 leading-relaxed max-w-3xl mx-auto">
              Contenu clair, Ads pilotées au profit, expansion UE/US sans friction.
            </p>
            <Button variant="cta-primary" size="lg" onClick={() => navigate('/contact')} className="group mb-8">
              Obtenir un audit
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
            
            {/* Scroll indicator */}
            <div className="flex flex-col items-center mt-12 lg:mt-16">
              <p className="text-sm text-muted-foreground mb-3">Découvrez notre approche</p>
              <div className="w-6 h-10 border-2 border-accent/30 rounded-full flex justify-center">
                <div className="w-1 h-3 bg-accent rounded-full mt-2 animate-bounce"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bullets Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6 mb-16">
              <Card className="border-accent/20 hover:border-accent/40 transition-colors">
                <CardContent className="p-6 text-center">
                  <Zap className="w-8 h-8 text-accent mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Refonte sémantique + A+ qui convertit</h3>
                </CardContent>
              </Card>
              <Card className="border-accent/20 hover:border-accent/40 transition-colors">
                <CardContent className="p-6 text-center">
                  <Target className="w-8 h-8 text-accent mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Architecture Ads (SP/SB/SD, DSP) orientée profit</h3>
                </CardContent>
              </Card>
              <Card className="border-accent/20 hover:border-accent/40 transition-colors">
                <CardContent className="p-6 text-center">
                  <Globe className="w-8 h-8 text-accent mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Expansion UE/US (listings, TVA/IOSS)</h3>
                </CardContent>
              </Card>
            </div>

            {/* Timeline */}
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <Card className="bg-gradient-to-br from-accent/10 to-accent/5 border-accent/20">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-accent mb-4">En 30 jours</h3>
                  <p className="text-muted-foreground">
                    Refonte listings, A+ v1, restructuration campagnes
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-primary mb-4">En 90 jours</h3>
                  <p className="text-muted-foreground">
                    A/B tests créa, Store v2, déploiement pays
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* What we activate */}
            <Card className="mb-16">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-6">Ce que nous activons</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                    <span>A+ & Stores cohérents, guidelines créa</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                    <span>Ads data-driven, dashboards clairs</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                    <span>Creative ops (UGC), process vitesse + cohérence</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CTA Group */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="cta-secondary" size="lg" asChild>
                <a href="https://cosmoswave-my.sharepoint.com/:b:/g/personal/k_mesloub_cosmos-wave_com/EQiYyYLs4ttBjfdFlU_SNEkBpChF7fvtxKcsjRHADDHBCA?e=Cid03X" target="_blank" rel="noopener noreferrer">
                  Télécharger la plaquette (PDF)
                </a>
              </Button>
              <Button variant="cta-outline" size="lg" onClick={() => navigate('/cases/creative')}>
                Voir des cas (NDA)
              </Button>
            </div>
          </div>
        </div>
      </section>
      </main>
    </>
  );
};

export default MarquesDnvb;